import { CircleMinus } from "lucide-react";
import { Updater, useImmer } from "use-immer";

import { IgIconButton, IgTypography } from "@intelligems/ig-theme";
import {
  AudienceFilterType,
  ExpressionNodeQueryType,
  ExpressionPayloadType,
  UrlMatch,
} from "@intelligems/ig-types";
import { Box, Grid } from "@mui/material";
import { TemporaryTargetingWarning } from "@src/components/Utils/TemporaryTargetingWarning";

import { TRAFFIC_CONFIG_QUERY_PARAM_OPTIONS } from "../../utils/constants";

import { ConditionFilter } from "./ConditionFilter";
import { ConditionHint } from "./ConditionHint";
import { ConditionKey } from "./ConditionKey";
import { ConditionTextKey } from "./ConditionTextKey";
import { ConditionType } from "./ConditionType";
import { ConditionValue } from "./ConditionValue";
import { useExpressionBuilderContext } from "./context";

interface ConditionProps {
  expressions: ExpressionPayloadType;
  setExpressions: Updater<ExpressionPayloadType>;
  expressionsIndex: number;
  removeExpression: (expressionsIndex: number) => void;
  configuredUrlPathFilters?: Map<UrlMatch, number>;
  configuredQueryParams?: Set<string>;
}

export const Condition = ({
  expressions,
  setExpressions,
  expressionsIndex,
  removeExpression,
  configuredUrlPathFilters,
  configuredQueryParams,
}: ConditionProps) => {
  const ctx = useExpressionBuilderContext();
  const [hover, setHover] = useImmer(false);

  const expression = {
    type: expressions.queries[expressionsIndex].type,
    filter: expressions.queries[expressionsIndex].filter,
    key: expressions.queries[expressionsIndex].key,
    value: expressions.queries[expressionsIndex].value || "",
  };

  const onConditionTypeChange = (type: AudienceFilterType) => {
    setExpressions((draft) => {
      draft.queries[expressionsIndex].type = type;
      draft.queries[expressionsIndex].value = "";
      draft.queries[expressionsIndex].key =
        type === "utm"
          ? Object.values(TRAFFIC_CONFIG_QUERY_PARAM_OPTIONS)[0]
          : "";
      draft.queries[expressionsIndex].filter =
        type === "urlPath" && configuredUrlPathFilters?.size
          ? "contains"
          : "equals";
    });
  };

  const onConditionObjectChange = <
    T extends Exclude<keyof ExpressionNodeQueryType, "type">,
  >(
    value: ExpressionNodeQueryType[T],
    changeKey: T
  ) => {
    setExpressions((draft) => {
      draft.queries[expressionsIndex][changeKey] = value;
      if (
        changeKey === "filter" &&
        (value === "isNull" || value === "isNotNull")
      ) {
        draft.queries[expressionsIndex].value = "";
      }

      return draft;
    });
  };

  return (
    <Grid
      wrap={"nowrap"}
      item
      container
      alignItems={"center"}
      columnSpacing={2}
      pb={2}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
    >
      {expressionsIndex > 0 && (
        <Grid item>
          <IgTypography ml={8} color="placeholder" sx={{ fontWeight: 500 }}>
            {ctx?.renderContext === "audience" ? "OR" : "AND"}
          </IgTypography>
        </Grid>
      )}
      <ConditionType
        tempExpressionQuery={expression}
        configuredUrlPathFilters={configuredUrlPathFilters}
        onChange={onConditionTypeChange}
      />
      {expression.type === "url" || expression.type === "utm" ? (
        <ConditionKey
          conditionKey={expression.key}
          onChange={onConditionObjectChange}
          configuredQueryParams={configuredQueryParams}
          viewOnly={ctx?.viewOnly}
        />
      ) : null}
      {expression.type === "cookie" && (
        <ConditionTextKey
          tempExpressionQuery={expression}
          onChange={onConditionObjectChange}
        />
      )}
      <ConditionFilter
        configuredUrlPathFilters={configuredUrlPathFilters}
        tempExpressionQuery={expression}
        onChange={onConditionObjectChange}
      />
      <ConditionValue
        onChange={onConditionObjectChange}
        tempExpressionQuery={expression}
      />
      <Grid item container xs={2} flexShrink={0} columnSpacing={1}>
        <Grid item>
          <ConditionHint expressionQuery={expression} />
        </Grid>
        {ctx?.renderContext === "audience" && (
          <TemporaryTargetingWarning
            expressions={expressions}
            expressionsIndex={expressionsIndex}
            isRolloutRender={ctx.isRolloutView}
          />
        )}
        <Grid
          item
          sx={{
            ...(!hover && { visibility: "hidden" }),
          }}
        >
          <IgIconButton onClick={() => removeExpression(expressionsIndex)}>
            <CircleMinus size={18} />
          </IgIconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
